import type { BaseProps } from '$lib/server/base-view-model/types';

export enum PricingAudienceFilter {
  All = 'filter_all',
  Restaurants = 'filter_restaurants',
  Retail = 'filter_retail',
  Beauty = 'filter_beauty',
  Services = 'filter_services',
}

type PricingFeatureFilter = {
  value: PricingAudienceFilter | undefined;
  label: string;
  image: ComponentContent;
};

type PricingFeatureTiers = {
  tier_free: string | boolean;
  tier_plus: string | boolean;
  tier_premium: string | boolean;
};

type PricingFeatureItem = {
  category: string;
  subcategory: string;
  category_key: string;
  subcategory_key: string;
  key?: string;
  feature: string;
  tooltip: string;
  tier_free: boolean | string;
  tier_plus: boolean | string;
  tier_premium: boolean | string;
  filter_restaurants: boolean;
  filter_retail: boolean;
  filter_beauty: boolean;
  filter_services: boolean;
  additional_keys: string[];
  top_all?: number | null;
  top_restaurants?: number | null;
  top_retail?: number | null;
  top_beauty?: number | null;
  top_services?: number | null;
};

type PricingFeatureCategory = {
  name: string;
  key: string;
  subcategories?: PricingFeatureCategory[];
  features?: PricingFeatureItem[];
};

type PricingFeaturesTableProps = BaseProps & {
  includeAudienceFilter: boolean;
  includeTopAudienceFeatures: boolean;
  forceAudience?: string;
  includedAudiences?: string[];
  audienceFilterOptions: PricingFeatureFilter[];
  tooltipLabel: string;
  ctaSubnavHeight: number;
  disableAccordion: boolean;
  topFeaturesLabels: {
    [PricingAudienceFilter.Restaurants]: string;
    [PricingAudienceFilter.Retail]: string;
    [PricingAudienceFilter.Beauty]: string;
    [PricingAudienceFilter.Services]: string;
  };
  pricingFeatures?: any[];
  processingFees?: any;
  subcategoryLinks: {
    subcatKey: string;
    link: ComponentContent;
  }[];

  // Contentful config option to render only these categories and their subcategories
  /*
    H1 Category
      H2 Subcategory 1
        Feature 1
        Feature 2
        ...
      H2 Subcategory 2
        ...
  */
  includedCategories?: string[];

  // Contentful config option to exclude certain categories
  /*
    H1 Category
      H2 Subcategory 1
        Feature 1
        Feature 2
        ...
    H1 Category 3 // excludes Category 2
        ...
  */
  excludedCategories?: string[];

  // Contentful config option to include only specific subcategories
  includedSubcategories?: string[];

  // Contentful config option to exclude certain subcategories
  /*
    H1 Category
      H2 Subcategory 1
        Feature 1
        Feature 2
        ...
      H2 Subcategory 3 // excludes Subcategory 2
        ...
  */
  excludedSubcategories?: string[];

  // Contentful config option to render only the included subcategories (styled like categories) and their features as a flat list
  /*
    H1 Subcategory 1
      Feature 1
      Feature 2
      ...
    H1 Subcategory 2
  */
  elevatedSubcategories?: string[];
};

export type {
  PricingFeatureFilter,
  PricingFeatureTiers,
  PricingFeatureItem,
  PricingFeatureCategory,
  PricingFeaturesTableProps,
};
